import {
  ALLOWED_PDF_EXTENSIONS,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select, Steps } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { todayLocal } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";

// Panel for "Consigner un visa entreprise générale envoyé hors ÉtudesCarré":
// record a visa addressed to a contract the user pilots (N102), then attach
// remarks to the documents that contract authored (N103).

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VisaFormData = {
  recipientContractId: number;
  dateEnvoi: string;
  numero: string;
};

type RemarkFormData = {
  description: string;
  documentVersionIds: number[];
};

type RecipientContract = {
  id: number;
  name: string;
  code: string | null;
  versions: {
    id: number;
    number: string;
    name: string;
    index: string | null;
  }[];
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

const STEP_ITEMS = [{ title: "Visa" }, { title: "Remarques" }];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ConsignerVisaPanelProps = {
  onClose: () => void;
};

export function ConsignerVisaPanel({ onClose }: ConsignerVisaPanelProps) {
  const { yard } = useYardContext();

  const [currentStep, setCurrentStep] = useState(0);
  const [visaId, setVisaId] = useState<number | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [contractError, setContractError] = useState<string | null>(null);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  const [recipients, setRecipients] = useState<RecipientContract[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [remarksAdded, setRemarksAdded] = useState(0);

  const [isSubmittingVisa, setIsSubmittingVisa] = useState(false);
  const [isSubmittingRemark, setIsSubmittingRemark] = useState(false);

  const visaForm = useForm<VisaFormData>({
    defaultValues: {
      recipientContractId: 0,
      dateEnvoi: todayLocal(),
      numero: "",
    },
  });

  const remarkForm = useForm<RemarkFormData>({
    defaultValues: { description: "", documentVersionIds: [] },
  });

  const selectedContract = visaForm.watch("recipientContractId");
  const selectedVersionIds = remarkForm.watch("documentVersionIds");

  const contractOptions = recipients.map((c) => ({
    label: `${c.name}${isNotNil(c.code) ? ` (${c.code})` : ""}`,
    value: String(c.id),
  }));
  const documentVersions =
    recipients
      .find((c) => c.id === selectedContract)
      ?.versions.map((version) => ({
        id: version.id,
        label: `${version.number} - ${version.name}${
          version.index ? ` (${version.index})` : ""
        }`,
      })) ?? [];

  useEffect(() => {
    let cancelled = false;
    async function fetchOptions() {
      try {
        const { data } = await apiClient.GET(
          "/api/yards/{yardId}/visas/options",
          { params: { path: { yardId: String(yard.id) } } },
        );
        if (!cancelled && isNotNil(data)) setRecipients(data.data.contracts);
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    }
    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [yard.id]);

  // -------------------------------------------------------------------------
  // Step 0: create the visa
  // -------------------------------------------------------------------------

  const handleVisaSubmit = visaForm.handleSubmit(async (formData) => {
    if (formData.recipientContractId <= 0) {
      setContractError("Sélectionnez un contrat");
      return;
    }

    setIsSubmittingVisa(true);
    try {
      const metadata = {
        recipientContractId: formData.recipientContractId,
        dateEnvoi: new Date(formData.dateEnvoi).toISOString(),
        ...(formData.numero ? { numero: formData.numero } : {}),
      };

      const { data: result, error } = await apiClient.POST(
        "/api/yards/{yardId}/visas",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: pdfFile ?? undefined,
          },
          bodySerializer(body) {
            const fd = new FormData();
            for (const [key, value] of Object.entries(
              body as Record<string, unknown>,
            )) {
              if (value instanceof File) {
                fd.append(key, value, value.name);
              } else if (isNotNil(value)) {
                fd.append(key, String(value));
              }
            }
            return fd;
          },
        },
      );

      if (error || isNil(result)) {
        toaster.error({
          title: "Erreur",
          description:
            error?.error ?? "Le visa n'a pas pu être enregistré, réessayez",
        });
        return;
      }

      setVisaId(result.data.visaId);
      setCurrentStep(1);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmittingVisa(false);
    }
  });

  // -------------------------------------------------------------------------
  // Step 1: add a remark linked to documents
  // -------------------------------------------------------------------------

  const handleRemarkSubmit = remarkForm.handleSubmit(async (formData) => {
    if (isNil(visaId)) return;

    if (formData.documentVersionIds.length === 0) {
      setDocumentsError("Sélectionnez au moins un document concerné");
      return;
    }

    setDocumentsError(null);
    setIsSubmittingRemark(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/visas/{visaId}/comments",
        {
          params: {
            path: { yardId: String(yard.id), visaId: String(visaId) },
          },
          body: {
            description: formData.description,
            documentVersionIds: formData.documentVersionIds,
          },
        },
      );

      if (error) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ?? "La remarque n'a pas pu être ajoutée, réessayez",
        });
        return;
      }

      setRemarksAdded((prev) => prev + 1);
      toaster.success({ title: "Succès", description: "Remarque ajoutée" });
      remarkForm.reset();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmittingRemark(false);
    }
  });

  // -------------------------------------------------------------------------
  // File handler
  // -------------------------------------------------------------------------

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPdfError(null);
    setPdfFile(null);
    if (isNil(file)) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      isNil(ext) ||
      !ALLOWED_PDF_EXTENSIONS.includes(
        ext as (typeof ALLOWED_PDF_EXTENSIONS)[number],
      )
    ) {
      setPdfError("Le fichier doit être au format PDF");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      setPdfError("Le fichier PDF dépasse 50 Mo");
      e.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  // -------------------------------------------------------------------------
  // Document version toggle
  // -------------------------------------------------------------------------

  const toggleVersion = (versionId: number) => {
    const current = selectedVersionIds;
    setDocumentsError(null);
    if (current.includes(versionId)) {
      remarkForm.setValue(
        "documentVersionIds",
        current.filter((id) => id !== versionId),
      );
    } else {
      remarkForm.setValue("documentVersionIds", [...current, versionId]);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <Steps.Root
        count={STEP_ITEMS.length}
        step={currentStep}
        onStepChange={(details) => setCurrentStep(details.step)}
        linear
      >
        <Steps.List>
          {STEP_ITEMS.map((item, index) => (
            <Steps.Item key={item.title} index={index}>
              <Steps.Trigger>
                <Steps.Indicator>{index + 1}</Steps.Indicator>
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  {item.title}
                </span>
              </Steps.Trigger>
              {index < STEP_ITEMS.length - 1 && <Steps.Separator />}
            </Steps.Item>
          ))}
        </Steps.List>

        {/* Step 0: Visa general info */}
        <Steps.Content index={0}>
          <form onSubmit={handleVisaSubmit} className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Informations du visa
            </h3>

            <div>
              <span className={labelClass}>
                Contrat <span className="text-red-500">*</span>
              </span>
              <Select
                options={contractOptions}
                value={selectedContract > 0 ? [String(selectedContract)] : []}
                onValueChange={(details) => {
                  const val = details.value[0];
                  setContractError(null);
                  visaForm.setValue(
                    "recipientContractId",
                    val ? Number(val) : 0,
                  );
                }}
                placeholder="Sélectionnez un contrat"
              />
              {!loadingRecipients && recipients.length === 0 && (
                <p className="mt-1 text-xs text-zinc-500">
                  Vous ne pilotez aucun contrat sur ce chantier.
                </p>
              )}
              {isNotNil(contractError) && (
                <p className="mt-1 text-xs text-red-600">{contractError}</p>
              )}
            </div>

            <div>
              <label htmlFor="visaDateEnvoi" className={labelClass}>
                Date d'envoi <span className="text-red-500">*</span>
              </label>
              <input
                id="visaDateEnvoi"
                type="date"
                {...visaForm.register("dateEnvoi")}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="visaNumero" className={labelClass}>
                N° du visa
              </label>
              <input
                id="visaNumero"
                type="text"
                {...visaForm.register("numero")}
                maxLength={255}
                className={inputClass}
                placeholder="Optionnel"
              />
            </div>

            <div>
              <label htmlFor="visaPdf" className={labelClass}>
                Fichier du visa (PDF)
              </label>
              <input
                id="visaPdf"
                type="file"
                accept=".pdf"
                onChange={handlePdfChange}
                className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
              />
              {isNotNil(pdfFile) && (
                <p className="mt-1 text-xs text-zinc-500">
                  {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} Mo)
                </p>
              )}
              {isNotNil(pdfError) && (
                <p className="mt-1 text-xs text-red-600">{pdfError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                outline
                onClick={onClose}
                disabled={isSubmittingVisa}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmittingVisa}>
                {isSubmittingVisa ? "En cours..." : "Suivant"}
              </Button>
            </div>
          </form>
        </Steps.Content>

        {/* Step 1: Remarks */}
        <Steps.Content index={1}>
          <form onSubmit={handleRemarkSubmit} className="space-y-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Ajouter une remarque
            </h3>

            {remarksAdded > 0 && (
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
                {remarksAdded} remarque(s) ajoutée(s)
              </div>
            )}

            <div>
              <label htmlFor="remarkDescription" className={labelClass}>
                Remarque <span className="text-red-500">*</span>
              </label>
              <textarea
                id="remarkDescription"
                {...remarkForm.register("description")}
                required
                minLength={1}
                maxLength={5000}
                rows={4}
                className={inputClass}
                placeholder="Texte de la remarque"
              />
            </div>

            <div>
              <span className={labelClass}>
                Documents concernés <span className="text-red-500">*</span>
              </span>
              {documentVersions.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  Aucun document de ce contrat ne vous est accessible
                </p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-zinc-300 p-2 dark:border-zinc-700">
                  {documentVersions.map((version) => (
                    <label
                      key={version.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedVersionIds.includes(version.id)}
                        onChange={() => toggleVersion(version.id)}
                      />
                      <span>{version.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {isNotNil(documentsError) && (
                <p className="mt-1 text-xs text-red-600">{documentsError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                outline
                onClick={onClose}
                disabled={isSubmittingRemark}
              >
                Terminer
              </Button>
              <Button type="submit" disabled={isSubmittingRemark}>
                {isSubmittingRemark ? "En cours..." : "Ajouter et continuer"}
              </Button>
            </div>
          </form>
        </Steps.Content>
      </Steps.Root>
    </div>
  );
}
